import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { Flex } from '@mantine/core';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardV2 as Card, Icon, Skeleton, Text, toast, Tooltip, Well } from '@feathr/components';
import GrowthCreditIcon from '@feathr/extender/components/GrowthCreditIcon/GrowthCreditIcon';
import { useAccount } from '@feathr/extender/state';
import { currencyFormatter } from '@feathr/extender/utils';

import * as styles from './GrowthCreditsStats.css';

interface IGrowthCreditsData {
  credit_balance: number;
  credits_earned: number;
  credits_redeemed: number;
  invoice_value: number;
  media_value: number;
}

export interface IGrowthCreditsStatsProps {
  refreshKey?: number;
}

interface IStatDisplayProps {
  isLoading?: boolean;
  label: string;
  tooltip?: string;
  value: string;
}

function StatDisplay({
  isLoading,
  label,
  tooltip,
  value,
}: Readonly<IStatDisplayProps>): JSX.Element {
  return (
    <div className={styles.statDisplay}>
      <Flex align={'center'} gap={'xs'}>
        <Text.DefaultMedium>{label}</Text.DefaultMedium>
        {tooltip && (
          <Tooltip title={tooltip}>
            <Icon icon={faInfoCircle} />
          </Tooltip>
        )}
      </Flex>
      <Flex align={'center'} gap={'xs'}>
        <span className={styles.statValue}>{isLoading ? <Skeleton width={50} /> : value}</span>
        <span className={styles.coinIcon}>
          <GrowthCreditIcon />
        </span>
      </Flex>
    </div>
  );
}

function GrowthCreditsStats({ refreshKey }: Readonly<IGrowthCreditsStatsProps>): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();
  const [stats, setStats] = useState<IGrowthCreditsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchStats = useCallback(async (): Promise<void> => {
    if (!account) {
      return;
    }
    setIsLoading(true);
    try {
      const data = await account.getGrowthCredits();
      setStats(data);
    } catch {
      toast(t('Failed to load growth credits stats'), { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [account]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats, refreshKey]);

  const balance = stats?.credit_balance ?? 0;
  const mediaValue = stats?.media_value ?? 0;
  const invoiceValue = stats?.invoice_value ?? 0;
  const creditsEarned = stats?.credits_earned ?? 0;
  const creditsRedeemed = stats?.credits_redeemed ?? 0;

  return (
    <Flex direction={'column'} gap={'sm'}>
      <Card contentClassName={styles.balanceCard} width={'full'}>
        <div className={styles.balanceRow}>
          <StatDisplay
            isLoading={isLoading}
            label={t('Balance available for use')}
            tooltip={t(
              'Growth Credits are automatically earned from donation form tips and applied to your marketing costs. No manual action needed.',
            )}
            value={balance.toLocaleString()}
          />
          <Well className={styles.well} layout={'vertical'} theme={'neutral'}>
            <Text.DefaultHeadingMedium>{t('Media Spend Value')}</Text.DefaultHeadingMedium>
            <Text.DefaultRegular>
              {isLoading ? <Skeleton width={60} /> : currencyFormatter.format(mediaValue)}
            </Text.DefaultRegular>
          </Well>
          <Well className={styles.well} layout={'vertical'} theme={'neutral'}>
            <Text.DefaultHeadingMedium>{t('Invoice Discount Value')}</Text.DefaultHeadingMedium>
            <Text.DefaultRegular>
              {isLoading ? <Skeleton width={60} /> : currencyFormatter.format(invoiceValue)}
            </Text.DefaultRegular>
          </Well>
        </div>
      </Card>
      <div className={styles.bottomRow}>
        {/* empty divs added to prevent Card styling adding margin-top to direct siblings. */}
        <div>
          <Card contentClassName={styles.statCard} width={'full'}>
            <StatDisplay
              isLoading={isLoading}
              label={t('Credits earned')}
              value={creditsEarned.toLocaleString()}
            />
          </Card>
        </div>
        <div>
          <Card contentClassName={styles.statCard} width={'full'}>
            <StatDisplay
              isLoading={isLoading}
              label={t('Credits spent')}
              value={creditsRedeemed.toLocaleString()}
            />
          </Card>
        </div>
      </div>
    </Flex>
  );
}

export default GrowthCreditsStats;
